import { useState, useRef, useId } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadFile } from '@/features/delivery-proof/api/deliveryProofApi';
import type { DeliveryProofAttachment } from '@/features/delivery-proof/types/delivery-proof.types';
import {
  DELIVERY_PROOF_MAX_FILE_PAGES,
  DELIVERY_PROOF_MAX_FILE_SIZE,
  getAttachmentErrorMessage,
  getAttachmentPages,
} from '@/features/delivery-proof/utils/attachments';

interface FileUploadFieldProps {
  deliveryProofId: string;
  value?: DeliveryProofAttachment; // Pièce jointe existante
  onChange: (attachment: DeliveryProofAttachment | undefined) => void;
  accept?: string;
  disabled?: boolean;
  size?: 'default' | 'sm';
  // Libellé au-dessus du champ : à fournir quand le champ est à côté d'un autre
  // champ libellé, pour que les deux s'alignent (grille en `tw-items-start`).
  label?: string;
}

/**
 * Composant réutilisable pour l'upload de fichiers avec preview
 * - Upload automatique vers l'API (un PDF y est converti page par page)
 * - Preview de la page 1 avec modal affichant toutes les pages
 * - Bouton suppression
 */
export const FileUploadField = ({
  deliveryProofId,
  value,
  onChange,
  accept = 'image/png, image/jpeg, application/pdf',
  disabled = false,
  size = 'default',
  label,
}: FileUploadFieldProps) => {
  const sm = size === 'sm';
  const inputId = useId();
  const [showModal, setShowModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadMutation = useUploadFile();
  const isUploading = uploadMutation.isLoading;

  const pages = value ? getAttachmentPages(value) : [];
  const acceptsPdf = accept.includes('application/pdf');
  const maxSizeMb = DELIVERY_PROOF_MAX_FILE_SIZE / 1024 / 1024;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      onChange(await uploadMutation.mutateAsync({ id: deliveryProofId, file }));
    } catch (error) {
      toast.error(getAttachmentErrorMessage(error));
    } finally {
      // Reset l'input pour permettre le re-upload du même fichier
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemove = () => {
    onChange(undefined);
  };

  return (
    <div className="tw-min-w-0">
      {label && (
        <label htmlFor={inputId} className="tw-text-xs tw-text-gray-500 tw-mb-1 tw-block">
          {label}
        </label>
      )}
      <div className="tw-flex tw-items-center tw-gap-2 tw-overflow-hidden">
        {/* Input file */}
        <input
          id={inputId}
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          disabled={disabled || isUploading}
          className={`tw-block tw-w-full tw-text-gray-900 tw-border tw-border-gray-200 tw-rounded-xl tw-cursor-pointer tw-bg-gray-50 focus:tw-outline-none file:tw-mr-4 file:tw-rounded-l-xl file:tw-border-0 file:tw-font-semibold file:tw-bg-affinicar-primary file:tw-text-white hover:file:tw-bg-affinicar-primary-dark disabled:tw-opacity-50 disabled:tw-cursor-not-allowed ${sm ? 'tw-text-sm file:tw-py-2 file:tw-px-3 file:tw-text-sm' : 'tw-text-base sm:tw-text-sm file:tw-py-3 file:tw-px-4 file:tw-text-sm'}`}
        />

        {/* Loader pendant upload */}
        {isUploading && (
          <div className="tw-animate-spin tw-w-5 tw-h-5 tw-border-2 tw-border-affinicar-primary tw-border-t-transparent tw-rounded-full tw-shrink-0" />
        )}

        {/* Preview + actions */}
        {value && !isUploading && (
          <>
            {/* Bouton supprimer */}
            <button
              type="button"
              onClick={handleRemove}
              disabled={disabled}
              className="tw-p-1 tw-text-red-600 hover:tw-text-red-800 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              title="Supprimer le fichier"
            >
              <X className="tw-w-5 tw-h-5" />
            </button>

            {/* Preview miniature (page 1) */}
            <button
              type="button"
              onClick={() => setShowModal(true)}
              disabled={disabled}
              className="tw-relative tw-shrink-0 tw-border tw-border-gray-200 tw-rounded-xl tw-overflow-hidden hover:tw-border-affinicar-primary tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
              title={pages.length > 1 ? `Voir les ${pages.length} pages` : 'Voir l\'image en grand'}
            >
              <img
                src={value.fileUrl}
                alt="Preview"
                className="tw-w-12 tw-h-12 tw-object-cover"
              />
              {pages.length > 1 && (
                <span className="tw-absolute tw-bottom-0 tw-inset-x-0 tw-bg-black/60 tw-text-white tw-text-[10px] tw-leading-4 tw-text-center">
                  {pages.length} pages
                </span>
              )}
            </button>
          </>
        )}
      </div>

      {/* Limites, affichées avant l'upload */}
      {!value && !isUploading && (
        <p className="tw-mt-1 tw-text-xs tw-text-gray-400">
          {acceptsPdf
            ? `PDF : ${DELIVERY_PROOF_MAX_FILE_PAGES} pages maximum, ${maxSizeMb} Mo maximum`
            : `${maxSizeMb} Mo maximum`}
        </p>
      )}

      {/* Modal agrandissement : toutes les pages */}
      {showModal && value && (
        <div
          className="tw-fixed tw-inset-0 tw-z-50 tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-75"
          onClick={() => setShowModal(false)}
        >
          <div className="tw-relative tw-max-w-4xl tw-max-h-[90vh] tw-p-4">
            <button
              className="tw-absolute tw-top-6 tw-right-6 tw-z-10 tw-text-white hover:tw-text-gray-300 tw-bg-black tw-bg-opacity-50 tw-rounded-full tw-p-2"
              onClick={() => setShowModal(false)}
            >
              <X className="tw-w-6 tw-h-6" />
            </button>
            <div
              className="tw-max-h-[85vh] tw-overflow-y-auto tw-flex tw-flex-col tw-items-center tw-gap-4"
              onClick={(e) => e.stopPropagation()}
            >
              {pages.map((page, index) => (
                <div key={page} className="tw-flex tw-flex-col tw-items-center tw-gap-1">
                  {pages.length > 1 && (
                    <span className="tw-text-xs tw-text-white">
                      Page {index + 1}/{pages.length}
                    </span>
                  )}
                  <img
                    src={page}
                    alt={`Page ${index + 1}`}
                    className="tw-max-w-full tw-max-h-[85vh] tw-object-contain"
                  />
                </div>
              ))}
              {value.originalFileUrl && (
                <a
                  href={value.originalFileUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="tw-text-sm tw-text-white tw-underline hover:tw-text-gray-300"
                >
                  Voir le PDF d'origine
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
